import React, { useCallback, useRef, useState } from 'react'
import IconButton from '@mui/material/IconButton'
import ContentPasteGoTwoToneIcon from '@mui/icons-material/ContentPasteGoTwoTone'
import { extractDigits } from '../constants/inviteCode'

const EMPTY_DIGITS = ['', '', '', '', '', '']

const InviteCodeInput = ({ onVerified, verifyCode, className = '' }) => {
  const [digits, setDigits] = useState(EMPTY_DIGITS)
  const [error, setError] = useState('')
  const [isVerifying, setIsVerifying] = useState(false)
  const inputRefs = useRef([])

  const focusIndex = (index) => {
    inputRefs.current[index]?.focus()
    inputRefs.current[index]?.select()
  }

  const submitCode = useCallback(async (code) => {
    if (code.length !== 6 || isVerifying) return

    setIsVerifying(true)
    setError('')

    try {
      if (verifyCode) {
        await verifyCode(code)
      }
      onVerified?.(code)
    } catch (err) {
      setError(err.message || 'Invalid code. Check your email and try again.')
      setDigits(EMPTY_DIGITS)
      focusIndex(0)
    } finally {
      setIsVerifying(false)
    }
  }, [isVerifying, onVerified, verifyCode])

  const applyDigits = useCallback((rawValue) => {
    const next = extractDigits(rawValue).split('')
    const padded = [...next, ...EMPTY_DIGITS].slice(0, 6)
    setDigits(padded)
    setError('')

    if (next.length === 6) {
      submitCode(next.join(''))
      return
    }

    focusIndex(Math.min(next.length, 5))
  }, [submitCode])

  const handleChange = (index, value) => {
    const digit = extractDigits(value).slice(-1)
    const next = [...digits]
    next[index] = digit
    setDigits(next)
    setError('')

    if (digit && index < 5) {
      focusIndex(index + 1)
    }

    if (next.every((d) => d !== '') && next.join('').length === 6) {
      submitCode(next.join(''))
    }
  }

  const handleKeyDown = (index, event) => {
    if (event.key === 'Backspace' && !digits[index] && index > 0) {
      event.preventDefault()
      focusIndex(index - 1)
    }

    if (event.key === 'ArrowLeft' && index > 0) {
      event.preventDefault()
      focusIndex(index - 1)
    }

    if (event.key === 'ArrowRight' && index < 5) {
      event.preventDefault()
      focusIndex(index + 1)
    }
  }

  const handlePaste = (event) => {
    event.preventDefault()
    applyDigits(event.clipboardData.getData('text'))
  }

  const handlePasteFromClipboard = async () => {
    try {
      const text = await navigator.clipboard.readText()
      applyDigits(text)
    } catch {
      setError('Unable to read clipboard. Paste manually with Ctrl+V / Cmd+V.')
    }
  }

  return (
    <div className={className}>
      <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
        {isVerifying ? 'Verifying code…' : 'Enter the 6-digit code from your email'}
      </p>

      <div className="flex items-center justify-center gap-2 sm:gap-3">
        <div className="flex gap-2 sm:gap-3" onPaste={handlePaste}>
          {digits.map((digit, index) => (
            <input
              key={index}
              ref={(el) => { inputRefs.current[index] = el }}
              type="text"
              inputMode="numeric"
              autoComplete={index === 0 ? 'one-time-code' : 'off'}
              maxLength={1}
              value={digit}
              aria-label={`Digit ${index + 1} of 6`}
              className={`w-11 h-14 sm:w-12 sm:h-16 text-center text-xl font-semibold rounded-lg border-2 bg-white dark:bg-gray-800 text-gray-900 dark:text-white transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                error
                  ? 'border-red-400 dark:border-red-500'
                  : 'border-gray-300 dark:border-gray-600'
              }`}
              disabled={isVerifying}
              onChange={(event) => handleChange(index, event.target.value)}
              onKeyDown={(event) => handleKeyDown(index, event)}
              onFocus={(event) => event.target.select()}
            />
          ))}
        </div>

        <IconButton
          onClick={handlePasteFromClipboard}
          aria-label="Paste code from clipboard"
          title="Paste from clipboard"
          sx={{ color: 'inherit' }}
        >
          <ContentPasteGoTwoToneIcon />
        </IconButton>
      </div>

      {error && (
        <p className="mt-3 text-sm text-red-600 dark:text-red-400" role="alert">
          {error}
        </p>
      )}
    </div>
  )
}

export default InviteCodeInput
